import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Toastify } from "../../components/Toaster";
import { ConsentedScopeSchema } from "./oauthClientsSlice";

/**
 * One MCP client a user has consented to — an `OAuthConsentStore_<tenant>` record as
 * shaped by `GET /customer-management/connections`. `UserId` is mapped to `Uid`
 * server-side so this keys the same way as every other customer-management table.
 */
export interface UserConnectionSchema {
  Uid?: string;
  ClientId?: string;
  ClientName?: string;
  ParentClientId?: string;
  ParentClientName?: string;
  ConsentedScopes?: ConsentedScopeSchema[];
  OidcScopes?: string[];
  CreatedDate?: string;
  UpdatedDate?: string;
  [key: string]: any;
}

export interface UserConnectionsState {
  connections: UserConnectionSchema[];
  isLoading: boolean;
  error: string | null;
  revoking: boolean;
}

const initialState: UserConnectionsState = {
  connections: [],
  isLoading: false,
  error: null,
  revoking: false
};

const userConnectionsSlice = createSlice({
  name: "userConnections",
  initialState,
  reducers: {
    fetchUserConnectionsRequest(state, _action: PayloadAction<string>) {
      state.isLoading = true;
      state.error = null;
      // Drop the previous user's rows immediately. Without this, switching from one
      // customer to another leaves the first user's consented clients rendered under
      // the table's loading overlay until the new response lands — one subject's
      // consent data shown on another subject's page.
      state.connections = [];
    },
    fetchUserConnectionsSuccess(state, action: PayloadAction<UserConnectionSchema[]>) {
      state.isLoading = false;
      state.connections = action.payload;
    },
    fetchUserConnectionsFailure(state, action: PayloadAction<string>) {
      state.isLoading = false;
      state.error = action.payload;
      Toastify("error", action.payload);
    },

    // The success toast is raised from the reducer to sit alongside the failure toast
    // below, but a reducer can't reach state.appState.i18n — so the caller passes the
    // localized string in with the request and the saga hands it to the outcome action.
    // Same route as deleteOAuthClientRequest.
    revokeUserConnectionRequest(state, _action: PayloadAction<{ uid: string; clientId: string; parentClientId: string; successMessage: string }>) {
      state.revoking = true;
    },
    revokeUserConnectionSuccess(state, action: PayloadAction<{ clientId: string; parentClientId: string; message: string }>) {
      state.revoking = false;
      state.connections = state.connections.filter((c) => !(c.ClientId === action.payload.clientId && c.ParentClientId === action.payload.parentClientId));
      Toastify("success", action.payload.message);
    },
    revokeUserConnectionFailure(state, action: PayloadAction<string>) {
      state.revoking = false;
      Toastify("error", action.payload);
    },

    resetUserConnectionsState() {
      return initialState;
    }
  }
});

export const {
  fetchUserConnectionsRequest,
  fetchUserConnectionsSuccess,
  fetchUserConnectionsFailure,
  revokeUserConnectionRequest,
  revokeUserConnectionSuccess,
  revokeUserConnectionFailure,
  resetUserConnectionsState
} = userConnectionsSlice.actions;

export default userConnectionsSlice.reducer;
